import {
    ACCOUNT_WIDE_CHANGE_NOT_ALLOWED,
    ORDERER_DUPLICATE_EMAIL_MESSAGE,
} from "../../common/exceptions";
import {
    HybrisFieldError,
    HybrisFieldValidationResponse,
    ListOption,
} from "../../common/types";
import { isHybrisValidationError } from "../../common/validations";
import { TableRowItem } from "../../components/Table/tableHelpers";
import {
    CHANGE_ROLE_FORBIDDEN_SUBTITLE,
    CHANGE_ROLE_NOT_FOUND_SUBTITLE,
    HYBRIS_INVALID_MUNICIPALITY_ROLE,
    HYBRIS_ORDERER_ADMIN_UNITS_REQUIRED,
    HYBRIS_UNITS_MUST_BELONG_TO_MUNICIPALITY,
    INVALID_MUNICIPALITY_ROLE_MESSAGE,
    ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE,
    UNITS_MUST_BELONG_TO_MUNICIPALITY_MESSAGE,
} from "../AdministratorView/administratorDetailsConfig";
import { OrdererInfoData } from "./OrdererInfoForm";
import {
    ChangeOrdererRoleInput,
    Orderer,
    OrdererInput,
    OrdererListingItem,
    OrdererRowDataType,
} from "./ordererTypes";
import { formatOrdererListingUnits } from "./OrdererView.utils";

export const ORDERER_UNITS_REQUIRED_MESSAGE = "En beställare måste tilldelas minst en enhet.";

export const ORDERER_ADMIN_OVERLAP_UNITS_HINT =
    "Beställaren kan även vara kopplad till enheter som du inte har behörighet att se. Dina ändringar påverkar inte kopplingarna till dessa enheter.";

export const DELETE_ORDERER_FORBIDDEN_SUBTITLE =
    "Du kan inte ta bort en beställare som också är kopplad till enheter du inte har tillgång till.";

export const ORDERER_EMAIL_ACCOUNT_WIDE_MESSAGE =
    "Du kan inte ändra e-postadressen för beställare som är kopplade till enheter du inte har tillgång till.";

export const ORDERER_CUSTOMER_CREATION_ACCOUNT_WIDE_MESSAGE =
    "Du kan inte ändra den här behörigheten för beställare som är kopplade till enheter du inte har tillgång till.";

export const ORDERER_EMAIL_ACCOUNT_WIDE_SUBTITLE =
    "Du saknar behörighet att ändra e-postadress för den här beställaren, eftersom hen även är kopplad till enheter du inte har tillgång till. Inga ändringar har sparats.";

export const ORDERER_CUSTOMER_CREATION_ACCOUNT_WIDE_SUBTITLE =
    "Du saknar behörighet att ändra om den här beställaren får lägga till nya slutkunder, eftersom hen även är kopplad till enheter du inte har tillgång till. Inga ändringar har sparats.";

export const ORDERER_ACCOUNT_WIDE_SUBTITLE =
    "Du saknar behörighet att ändra e-postadress och behörigheten att lägga till nya slutkunder för den här beställaren, eftersom hen även är kopplad till enheter du inte har tillgång till. Inga ändringar har sparats.";

export const ordererRoleLabel = (role: "ORDERER" | "ORDERER_ADMIN") =>
    role === "ORDERER_ADMIN" ? "Beställare administratör" : "Beställare";

export interface OrdererAccountWideErrors {
    email?: string;
    customerCreationAllowed?: string;
}

const ACCOUNT_WIDE_FIELD_MESSAGES: Record<keyof OrdererAccountWideErrors, string> = {
    email: ORDERER_EMAIL_ACCOUNT_WIDE_MESSAGE,
    customerCreationAllowed: ORDERER_CUSTOMER_CREATION_ACCOUNT_WIDE_MESSAGE,
};

const isAccountWideError = (error: HybrisFieldError) =>
    error.reason === ACCOUNT_WIDE_CHANGE_NOT_ALLOWED;

const getAccountWideFieldMessage = (subject: string) =>
    ACCOUNT_WIDE_FIELD_MESSAGES[subject as keyof OrdererAccountWideErrors];

/**
 * Collects the fields the current user is not allowed to change account wide,
 * so they can be shown next to the field instead of as form validation errors.
 */
export const getOrdererAccountWideErrors = (error: unknown): OrdererAccountWideErrors => {
    if (!isHybrisValidationError(error)) {
        return {};
    }

    return error.errors.reduce<OrdererAccountWideErrors>((result, current) => {
        if (!isAccountWideError(current)) {
            return result;
        }

        const message = getAccountWideFieldMessage(current.subject);

        return message ? { ...result, [current.subject]: message } : result;
    }, {});
};

/**
 * Account wide errors are shown next to their field, so they must not also be
 * set as form errors. That would leave the form invalid on a field the user
 * cannot change, blocking any other pending edit from being saved.
 */
export const withoutAccountWideErrors = (error: unknown) => {
    if (!isHybrisValidationError(error)) {
        return error;
    }

    return {
        ...error,
        errors: error.errors.filter((current) => !isAccountWideError(current)),
    };
};

export const getDeleteOrdererErrorSubtitle = (error: unknown) => {
    if (error instanceof Response && error.status === 403) {
        return DELETE_ORDERER_FORBIDDEN_SUBTITLE;
    }

    return undefined;
};

export const ordererRowData = (data: OrdererListingItem[]) =>
    data.map<TableRowItem<OrdererRowDataType>>((orderer) => ({
        data: {
            ...orderer,
            id: orderer.orderer.uid,
        },
        rowValues: [
            {
                sortPredicate: `${orderer.orderer.firstName} ${orderer.orderer.lastName}`,
                value: `${orderer.orderer.firstName} ${orderer.orderer.lastName}`,
            },
            {
                sortPredicate: `${orderer.orderer.municipalEmail}`,
                value: `${orderer.orderer.municipalEmail}`,
            },
            {
                sortPredicate: orderer.authUnits?.length ? `${orderer.authUnits[0].name}` : "",
                value: formatOrdererListingUnits(orderer.authUnits),
            },
        ],
    }));

export const convertOrdererFromOutput = (data?: Orderer) =>
    ({
        firstName: data?.firstName || "",
        lastName: data?.lastName || "",
        phone: data?.municipalPhoneNumber || "",
        email: data?.municipalEmail || "",
        customerCreationAllowed: data?.customerCreationAllowed || false,
        municipalityRole:
            data?.municipalityRole === "ORDERER_ADMIN" ? "ORDERER_ADMIN" : "ORDERER",
    }) as OrdererInfoData;

export const convertOrdererForInput = (formData: OrdererInfoData, units: ListOption[]) =>
    ({
        firstName: formData?.firstName || "",
        lastName: formData.lastName || "",
        municipalityPhone: formData?.phone || "",
        email: formData?.email || "",
        customerCreationAllowed: formData.customerCreationAllowed,
        unitIds: units.map((unit) => unit.id),
    }) as OrdererInput;

export const convertOrdererForPromote = (units: ListOption[]): ChangeOrdererRoleInput => ({
    municipalityRole: "ORDERER_ADMIN",
    unitIds: units.map((unit) => unit.id),
});

/**
 * Hybris returns erros with different models
 * This function is to map known exceptions to the form field names
 */
export const mapHybrisOrdererErrorField = (
    args: HybrisFieldError,
): { fieldName: string; message: string } => {
    if (args.message === ORDERER_DUPLICATE_EMAIL_MESSAGE) {
        return {
            fieldName: "email",
            message: "E-postadressen finns redan registrerad",
        };
    }

    if (args.message === HYBRIS_ORDERER_ADMIN_UNITS_REQUIRED) {
        return {
            fieldName: "units",
            message: ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE,
        };
    }

    if (args.message === HYBRIS_UNITS_MUST_BELONG_TO_MUNICIPALITY) {
        return {
            fieldName: "units",
            message: UNITS_MUST_BELONG_TO_MUNICIPALITY_MESSAGE,
        };
    }

    if (args.message === HYBRIS_INVALID_MUNICIPALITY_ROLE) {
        return {
            fieldName: "municipalityRole",
            message: INVALID_MUNICIPALITY_ROLE_MESSAGE,
        };
    }

    if (isAccountWideError(args)) {
        return {
            fieldName: args.subject,
            message: getAccountWideFieldMessage(args.subject) || "",
        };
    }

    return { fieldName: args.subject, message: "" };
};

const getHttpStatusFromError = (error: unknown): number | undefined => {
    if (error instanceof Response) {
        return error.status;
    }

    if (
        error &&
        typeof error === "object" &&
        "httpStatus" in error &&
        typeof (error as { httpStatus?: unknown }).httpStatus === "number"
    ) {
        return (error as { httpStatus: number }).httpStatus;
    }

    return undefined;
};

export const getChangeOrdererRoleErrorSubtitle = (error: unknown) => {
    if (isHybrisValidationError(error)) {
        return getModalOrdererErrorText(error);
    }

    const httpStatus = getHttpStatusFromError(error);

    if (httpStatus === 401 || httpStatus === 403) {
        return CHANGE_ROLE_FORBIDDEN_SUBTITLE;
    }

    if (httpStatus === 404) {
        return CHANGE_ROLE_NOT_FOUND_SUBTITLE;
    }

    return undefined;
};

export const getModalOrdererErrorText = (response: HybrisFieldValidationResponse) => {
    if (!response?.errors) {
        return undefined;
    }

    if (response.errors.find((error) => error.message === ORDERER_DUPLICATE_EMAIL_MESSAGE)) {
        return "En användare med den e-postadressen du uppgav existerar redan.";
    }

    const accountWideErrors = getOrdererAccountWideErrors(response);

    if (accountWideErrors.email && accountWideErrors.customerCreationAllowed) {
        return ORDERER_ACCOUNT_WIDE_SUBTITLE;
    }

    if (accountWideErrors.email) {
        return ORDERER_EMAIL_ACCOUNT_WIDE_SUBTITLE;
    }

    if (accountWideErrors.customerCreationAllowed) {
        return ORDERER_CUSTOMER_CREATION_ACCOUNT_WIDE_SUBTITLE;
    }

    const knownPromoteError = response.errors.find(
        (error) =>
            error.message === HYBRIS_ORDERER_ADMIN_UNITS_REQUIRED ||
            error.message === HYBRIS_UNITS_MUST_BELONG_TO_MUNICIPALITY ||
            error.message === HYBRIS_INVALID_MUNICIPALITY_ROLE,
    );

    if (knownPromoteError) {
        return mapHybrisOrdererErrorField(knownPromoteError).message;
    }

    return undefined;
};
